import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  FirestoreDataConverter,
} from 'firebase/firestore';
import type { Audience } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { ActorService } from './ActorService';
import { audienceConverter as clientAudienceConverter } from '../converters/audienceConverter';
import { actorConverter } from '../converters/actorConverter';

/**
 * AudienceService
 *
 * Type-safe service for Audience entity CRUD operations.
 * Audiences are stored as subcollection under projects.
 *
 * Path: projects/{projectId}/audiences/{audienceId}
 *
 * Features:
 * - Basic CRUD operations (create, read, update, delete, list)
 * - Type-safe: only Audience types exposed, no Firebase types in public API
 * - Subcollection access via getActorsService()
 * - Uses audienceConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { AudienceService } from '@/modules/firebase-core/services/AudienceService';
 *
 * const service = new AudienceService('project-id');
 *
 * // Create
 * const audience = await service.create({
 *   name: 'Target Audience',
 *   description: 'Description',
 *   weight: 1
 * });
 *
 * // Read
 * const found = await service.get(audience.id!);
 *
 * // Update
 * await service.update(audience.id!, { description: 'Updated' });
 *
 * // List
 * const audiences = await service.list({ limit: 20 });
 *
 * // Delete
 * await service.delete(audience.id!);
 *
 * // Access actors subcollection
 * const actorService = service.getActorsService(audience.id!);
 * const actors = await actorService.list();
 * ```
 */
export class AudienceService {
  private readonly collectionName = 'audiences';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Audience>;
  private readonly projectId: string;

  /**
   * Create a new AudienceService instance
   *
   * @param projectId - Parent project ID
   * @param converter - Firestore converter for Audience entity (defaults to client SDK converter)
   */
  constructor(projectId: string, converter: FirestoreDataConverter<Audience> = clientAudienceConverter) {
    this.projectId = projectId;
    this.converter = converter;
  }

  /**
   * Get the subcollection path for audiences
   */
  private getCollectionPath(): string {
    return `projects/${this.projectId}/${this.collectionName}`;
  }

  /**
   * Create a new audience
   *
   * @param data - Partial audience data (id will be auto-generated if not provided)
   * @returns {Promise<Audience>} The created audience with generated ID
   */
  async create(data: Partial<Audience>): Promise<Audience> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath());
      const docRef = doc(collectionRef).withConverter(this.converter);

      const audienceData: Audience = {
        ...data,
        id: docRef.id,
        creationDate: new Date(),
        updatedAt: new Date(),
      } as Audience;

      await setDoc(docRef, audienceData);

      return audienceData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'AudienceService.create');
      throw error;
    }
  }

  /**
   * Get an audience by ID
   *
   * @param id - Audience ID
   * @returns {Promise<Audience | null>} The audience or null if not found
   */
  async get(id: string): Promise<Audience | null> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Audience;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'AudienceService.get');
      throw error;
    }
  }

  /**
   * Update an existing audience
   *
   * CRITICAL: Uses setDoc with merge (NOT updateDoc) to ensure converter is applied
   *
   * @param id - Audience ID
   * @param data - Partial audience data to update
   * @returns {Promise<Audience>} The updated audience
   */
  async update(id: string, data: Partial<Audience>): Promise<Audience> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use setDoc with merge: true instead of updateDoc
      // This ensures the converter's toFirestore is called correctly
      await setDoc(docRef, updateData as Audience, { merge: true });

      const updated = await this.get(id);
      if (!updated) {
        throw new Error(`Audience ${id} not found after update`);
      }

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'AudienceService.update');
      throw error;
    }
  }

  /**
   * Delete an audience
   *
   * Note: Does NOT cascade delete actors subcollection
   * Use cascade delete if needed to also delete actors
   *
   * @param id - Audience ID
   * @returns {Promise<void>}
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'AudienceService.delete');
      throw error;
    }
  }

  /**
   * List audiences for the project
   *
   * @param options - Query options
   * @param options.limit - Maximum number of audiences to return (default: 20)
   * @returns {Promise<Audience[]>} Array of audiences
   */
  async list(options: { limit?: number } = {}): Promise<Audience[]> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath()).withConverter(this.converter);

      const pageLimit = options.limit || 20;
      const q = query(collectionRef, orderBy('creationDate', 'desc'), limit(pageLimit));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Audience));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'AudienceService.list');
      throw error;
    }
  }

  /**
   * List audiences with all nested data (actors + subcollections)
   *
   * This method loads:
   * - All audiences for the project
   * - All actors for each audience
   * - All subcollections for each actor (coreJobs, fears, contexts, etc.)
   *
   * @param onProgress - Optional callback for progress tracking
   * @returns {Promise<Audience[]>} Complete audiences with nested data
   *
   * @example
   * ```typescript
   * const audiences = await service.listWithActorsAndSubcollections(
   *   (loaded, total, item) => console.log(`${loaded}/${total}: ${item}`)
   * );
   * ```
   */
  async listWithActorsAndSubcollections(
    onProgress?: (loaded: number, total: number, currentItem: string) => void
  ): Promise<Audience[]> {
    try {
      // Step 1: Load all audiences
      const audiences = await this.list();

      if (audiences.length === 0) {
        return audiences;
      }

      // Calculate total steps: audiences + (actors per audience)
      let totalSteps = audiences.length;
      let currentStep = 0;

      // Step 2: Load actors and subcollections for each audience
      for (const audience of audiences) {
        currentStep++;
        onProgress?.(currentStep, totalSteps, `Loading audience: ${audience.name || audience.id}`);

        // Get actor service for this audience
        const actorService = this.getActorsService(audience.id!);

        // Load all actors
        const actors = await actorService.list();

        // Load subcollections for each actor
        const actorsWithSubcollections = await Promise.all(
          actors.map(async (actor) => {
            return await actorService.getWithSubcollections(actor.id!);
          })
        );

        // Filter out nulls and assign to audience
        audience.actors = actorsWithSubcollections.filter(a => a !== null);
      }

      onProgress?.(totalSteps, totalSteps, 'Loading complete');
      return audiences;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'AudienceService.listWithActorsAndSubcollections');
      throw error;
    }
  }

  /**
   * Get ActorService for an audience's actors subcollection
   *
   * @param audienceId - Audience ID
   * @returns {ActorService} ActorService instance scoped to this audience
   *
   * @example
   * ```typescript
   * const actorService = audienceService.getActorsService('audience-123');
   *
   * // Now use actorService for actors in this audience
   * const actor = await actorService.create({ name: 'Actor Name' });
   * const actors = await actorService.list();
   * ```
   */
  getActorsService(audienceId: string): ActorService {
    return new ActorService(this.projectId, audienceId, actorConverter);
  }
}
